import React from 'react';
import Media, { PROVIDER_TYPES } from '../../../helpers/media';
import styled from 'styled-components';
import { Typo } from '../../../ui';

const SimplerEmbed = styled.div`
  width: 100%;
  iframe {
    max-height: ${({ $isVideo }) => ($isVideo ? '450px' : 'unset')};
    width: 100% !important;
  }
  & :first-child {
    min-width: 0 !important;
  }
`;

const Container = styled.div`
  align-self: start;
  flex-basis: ${props => props.$split50 && '50%'};
  width: 100%;
`;

const IframeWrapper = styled.div`
  width: 100%;
  margin: auto;
  display: block;
  max-width: 100%;
  position: relative;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  height: 0;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const IframeEmbedder = ({ url, id, urlSuffix = '' }) => {
  const baseUrl = url && url.startsWith('//') ? `https:${url}` : url;

  let fullUrl = baseUrl;
  if (id) fullUrl = fullUrl + id;
  if (urlSuffix) fullUrl = fullUrl + urlSuffix;

  return (
    <IframeWrapper>
      <iframe
        title={'UniqOne'}
        src={fullUrl}
        allow='autoplay; fullscreen; picture-in-picture'
        allowFullScreen
      />
    </IframeWrapper>
  );
};

const YouTube = ({ id }) => <IframeEmbedder url={Media.baseUrls[PROVIDER_TYPES.YOUTUBE]} id={id} />;
const Vimeo = ({ id }) => <IframeEmbedder url={Media.baseUrls[PROVIDER_TYPES.VIMEO]} id={id} />;
const Wistia = ({ id }) => <IframeEmbedder url={Media.baseUrls[PROVIDER_TYPES.WISTIA]} id={id} />;

const Docs = ({ data, provider }) => {
  const url = Media.getUrlFromProvider({ provider, id: data });
  return <IframeEmbedder url={url} />
};

const SoundCloud = ({ id, type }) => {
  if (type === 'track') {
    return (
      <IframeEmbedder
        type="audio"
        url={Media.baseUrls[PROVIDER_TYPES.SOUNDCLOUD]}
        id={id}
      />
    );
  } else {
    return <div dangerouslySetInnerHTML={{ __html: id }} />;
  }
};

const Spotify = ({ id, user, type }) => {
  const url = user ? `user:${user}:${type}:${id}` : id;
  return <IframeEmbedder url={Media.baseUrls[PROVIDER_TYPES.SPOTIFY]} id={url} />;
};

const Youku = ({ id }) => (
  <IframeEmbedder
    url={Media.baseUrls[PROVIDER_TYPES.YOUKU]}
    id={id}
    allowFullScreen
  />
);

const providers = {
  [PROVIDER_TYPES.YOUTUBE]: YouTube,
  [PROVIDER_TYPES.VIMEO]: Vimeo,
  [PROVIDER_TYPES.SOUNDCLOUD]: SoundCloud,
  [PROVIDER_TYPES.SPOTIFY]: Spotify,
  [PROVIDER_TYPES.WISTIA]: Wistia,
  [PROVIDER_TYPES.YOUKU]: Youku,
  [PROVIDER_TYPES.LINK]: Docs,
};

const knownVideoProviders = {
  youtube: /youtube\.com|youtu\.be/,
  vimeo: /vimeo\.com/,
  spotify: /spotify\.com/,
  wistia: /wistia\.com|wistia\.net/,
  youku: /youku\.com/,
};

const EmbedModule = React.memo(({ data, params, split50 }) => {

  if(!data && !params) return null

  const isKnownVideoProvider = (html) => {
    const iframeMatch = html.match(/<iframe[^>]+src=["']([^"']+)["']/i);
    if (!iframeMatch) return false;

    const src = iframeMatch[1];
    return Object.values(knownVideoProviders).some((regex) => regex.test(src));
  };

  const source = !!data?.sources && data?.sources[0]
  const provider = source?.provider || params?.provider

  const Provider = providers[provider];

  const isVideo = provider === PROVIDER_TYPES.EMBED && source?.data && isKnownVideoProvider(source?.data);

  return (
    <Container $split50={split50}>
      {Provider ? <Provider {...(params ? params : { provider, data: source.data })} />
      : provider === PROVIDER_TYPES.EMBED && <SimplerEmbed $isVideo={isVideo} dangerouslySetInnerHTML={{ __html: source.data }} />}
      {data?.params?.caption && <Typo.SmallText>{data.params.caption[0]}</Typo.SmallText>}
    </Container>
  );
});

export default EmbedModule;
